import React from "react";
import EditLoan from "../../MyCashFlow/Loan/EditLoan";
import { getCurrentFinancialYear } from "../../../../utils/commonUtil";
interface LoanProps {
  cashFlowData: any;
  year: string;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
  isShowingTotal?: boolean;
}

const formatCurrency = (value: string | number) => {
  const num = Number(value);
  if (isNaN(num) || value === "") return "$0";
  if (num < 0) {
    return (
      <span style={{ color: "red" }}>
        ({Math.abs(num).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })})
      </span>
    );
  }
  return num.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 });
};

const Loan: React.FC<LoanProps> = ({ cashFlowData, year, isEditable, setIsEditable, isShowingTotal }) => {
  const portion = isShowingTotal ? cashFlowData?.CashflowLoanList : cashFlowData?.CashflowLoanListPortion;
  const totals = isShowingTotal
    ? cashFlowData?.CashflowLoanList?.LoansTotals
    : cashFlowData?.CashflowLoanListPortion?.LoansTotals;
  const currentYear = "20" + Number(getCurrentFinancialYear()).toString();

  if (!portion) return <div>No loan data available.</div>;

  // Find the index for the given year in Forecast (all arrays are parallel)
  const forecastYearIdx = portion.Forecast.findIndex((arr: any[]) => arr[0]?.Year === year);
  const actualsYearIdx = portion.Actuals.findIndex((arr: any[]) => arr[0]?.Year === year);
  const varianceYearIdx = portion.Variance.findIndex((arr: any[]) => arr[0]?.Year === year);

  // Get the arrays for the year
  const forecastLoans = forecastYearIdx !== -1 ? portion.Forecast[forecastYearIdx] : [];
  const actualsLoans = actualsYearIdx !== -1 ? portion.Actuals[actualsYearIdx] : [];
  const varianceLoans = varianceYearIdx !== -1 ? portion.Variance[varianceYearIdx] : [];

  // Get all unique loan names for the year
  // in variance some time name is not present
  const loanNames = Array.from(
    new Set([
      ...forecastLoans.map((l: any) => (l.Name == "" ? "Loan 1" : l.Name)),
      ...actualsLoans.map((l: any) => (l.Name == "" ? "Loan 1" : l.Name)),
      ...varianceLoans.map((l: any) => (l.Name == "" ? "Loan 1" : l.Name)),
    ])
  ).filter(Boolean);

  // Helper to get loan by name
  // instead of name we can use loan id to get the loan
  const getLoan = (arr: any[], name: string) => {
    return arr.find((l: any) => l.Name === name);
  };

  // Totals/Averages for the year
  const totalsForecast = totals?.Forecast?.find((t: any) => t.Year === year);
  const totalsActuals = totals?.Actuals?.find((t: any) => t.Year === year);
  const totalsVariance = totals?.Variance?.find((t: any) => t.Year === year);

  return (
    <div>
      {isEditable && (
        <EditLoan
          cashFlowData={cashFlowData}
          year={year}
          propertySelected={cashFlowData.PortfolioTrackerDetailLabel.PortfolioTrackerId}
          propertyName={`${cashFlowData.PortfolioTrackerDetailLabel.StreetAddress}, ${cashFlowData.PortfolioTrackerDetailLabel.Suburb}, ${cashFlowData.PortfolioTrackerDetailLabel.State}, ${cashFlowData.PortfolioTrackerDetailLabel.Postcode} (${cashFlowData.PortfolioTrackerDetailLabel.Country})`}
          setIsEditable={setIsEditable}
          onCancel={() => setIsEditable(false)}
        />
      )}
      {!isEditable && (
        <>
          <div className="mb-4 text-start font-semibold">
            {cashFlowData.PortfolioTrackerDetailLabel.StreetAddress}, {cashFlowData.PortfolioTrackerDetailLabel.Suburb},{" "}
            {cashFlowData.PortfolioTrackerDetailLabel.State}, {cashFlowData.PortfolioTrackerDetailLabel.Postcode} (
            {cashFlowData.PortfolioTrackerDetailLabel.Country})
          </div>
          <table className="loan-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                <th></th>
                <th>Type</th>
                <th>Loan start</th>
                <th>Term</th>
                <th>Lender/Name</th>
                <th>Rate review</th>
                <th>Interest rate</th>
                <th style={{ textAlign: "end" }}>Interest</th>
                <th style={{ textAlign: "end" }}>Principal</th>
                <th style={{ textAlign: "end" }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {loanNames.map((name, idx) => {
                const forecastLoan = forecastLoans.find((l: any, vIdx: number) => vIdx === idx) || {};
                const actualsLoan = actualsLoans.find((l: any, vIdx: number) => vIdx === idx) || {};
                const varianceLoan = varianceLoans.find((l: any, vIdx: number) => vIdx === idx) || {};
                return (
                  <React.Fragment key={name || idx}>
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          fontWeight: "bold",
                          textDecoration: name ? undefined : "underline",
                          textAlign: "start",
                        }}
                      >
                        {name}
                      </td>
                    </tr>
                    {/* Forecast row */}
                    <tr>
                      <td>Forecast</td>
                      <td>{forecastLoan?.MortgageType || ""}</td>
                      <td>{forecastLoan?.LoanStart || ""}</td>
                      <td>
                        {forecastLoan?.Term
                          ? parseFloat(forecastLoan?.Term) % 1 === 0
                            ? parseFloat(forecastLoan?.Term).toFixed(0) + " yrs"
                            : parseFloat(forecastLoan?.Term).toFixed(1) + " yrs"
                          : ""}
                      </td>
                      <td>
                        {forecastLoan?.Name +
                          (forecastLoan?.Name && forecastLoan?.RefCode ? "(" : "") +
                          forecastLoan?.RefCode +
                          (forecastLoan?.Name && forecastLoan?.RefCode ? ")" : "") || ""}
                      </td>
                      <td>{forecastLoan?.InterestRateReview || ""}</td>
                      <td>{forecastLoan?.InterestRate ? Number(forecastLoan?.InterestRate).toFixed(2) + "%" : ""}</td>
                      <td style={{ textAlign: "end" }}>{formatCurrency(forecastLoan?.InterestPayment)}</td>
                      <td style={{ textAlign: "end" }}>{formatCurrency(forecastLoan?.PrincipalPayment)}</td>
                      <td style={{ textAlign: "end" }}>{formatCurrency(forecastLoan?.Balance)}</td>
                    </tr>
                    {/* Actuals row */}
                    {Number(currentYear) >= Number(year) ? (
                      <tr>
                        <td>Actuals</td>
                        <td>{actualsLoan?.MortgageType || ""}</td>
                        <td>{actualsLoan?.LoanStart || ""}</td>
                        <td>
                          {actualsLoan?.Term
                            ? parseFloat(actualsLoan?.Term) % 1 === 0
                              ? parseFloat(actualsLoan?.Term).toFixed(0) + " yrs"
                              : parseFloat(actualsLoan?.Term).toFixed(1) + " yrs"
                            : ""}
                        </td>
                        <td>{""}</td>
                        <td>{actualsLoan?.InterestRateReview || ""}</td>
                        <td>{actualsLoan?.InterestRate ? Number(actualsLoan?.InterestRate).toFixed(2) + "%" : ""}</td>
                        <td style={{ textAlign: "end" }}>{formatCurrency(actualsLoan?.InterestPayment)}</td>
                        <td style={{ textAlign: "end" }}>{formatCurrency(actualsLoan?.PrincipalPayment)}</td>
                        <td style={{ textAlign: "end" }}>{formatCurrency(actualsLoan?.Balance)}</td>
                      </tr>
                    ) : (
                      <tr>
                        <td>Actuals</td>
                      </tr>
                    )}
                    {/* Variance row */}
                    {Number(currentYear) >= Number(year) ? (
                      <tr>
                        <td>Variance</td>
                        <td>{varianceLoan.MortgageType || ""}</td>
                        <td>{varianceLoan.LoanStart || ""}</td>
                        <td>
                          {varianceLoan?.Term
                            ? parseFloat(varianceLoan?.Term) % 1 === 0
                              ? parseFloat(varianceLoan?.Term).toFixed(0) + " yrs"
                              : parseFloat(varianceLoan?.Term).toFixed(1) + " yrs"
                            : ""}
                        </td>
                        <td>{""}</td>
                        <td>{varianceLoan.InterestRateReview || ""}</td>
                        <td>{varianceLoan.InterestRate ? Number(varianceLoan.InterestRate).toFixed(2) + "%" : ""}</td>
                        <td style={{ textAlign: "end" }}>{formatCurrency(varianceLoan.InterestPayment)}</td>
                        <td style={{ textAlign: "end" }}>{formatCurrency(varianceLoan.PrincipalPayment)}</td>
                        <td style={{ textAlign: "end" }}>{formatCurrency(varianceLoan.Balance)}</td>
                      </tr>
                    ) : (
                      <tr>
                        <td>Variance</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {/* Totals/Averages section */}
              {loanNames.length > 1 && (
                <>
                  <tr>
                    <td colSpan={9} style={{ fontWeight: "bold", textAlign: "start" }}>
                      Totals/Averages
                    </td>
                  </tr>
                  <tr>
                    <td>Forecast</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>{totalsForecast?.InterestRate ? Number(totalsForecast.InterestRate).toFixed(2) + "%" : ""}</td>
                    <td style={{ textAlign: "end" }}>{formatCurrency(totalsForecast?.InterestPayment)}</td>
                    <td style={{ textAlign: "end" }}>{formatCurrency(totalsForecast?.PrincipalPayment)}</td>
                    <td style={{ textAlign: "end" }}>{formatCurrency(totalsForecast?.Balance)}</td>
                  </tr>
                  <tr>
                    <td>Actuals</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>{totalsActuals?.InterestRate ? Number(totalsActuals.InterestRate).toFixed(2) + "%" : ""}</td>
                    <td style={{ textAlign: "end" }}>{formatCurrency(totalsActuals?.InterestPayment)}</td>
                    <td style={{ textAlign: "end" }}>{formatCurrency(totalsActuals?.PrincipalPayment)}</td>
                    <td style={{ textAlign: "end" }}>{formatCurrency(totalsActuals?.Balance)}</td>
                  </tr>
                  <tr>
                    <td>Variance</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      {totalsVariance?.InterestRate ? Number(totalsVariance.InterestRate).toFixed(2) + "%" : "0.00%"}
                    </td>
                    <td style={{ textAlign: "end" }}>{formatCurrency(totalsVariance?.InterestPayment)}</td>
                    <td style={{ textAlign: "end" }}>{formatCurrency(totalsVariance?.PrincipalPayment)}</td>
                    <td style={{ textAlign: "end" }}>{formatCurrency(totalsVariance?.Balance)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default Loan;
